import { Box, Button, Flex, Separator, Table, Text } from '@radix-ui/themes'
import { Link, useLocation } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { type Dispatch, type FC, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import { appConfig } from '#app/app-config.ts'
import { TransportFree } from '#app/components/icon'
import { Price } from '#app/components/ui/price/price.tsx'
import { type Locale } from '#app/models/locale.ts'
import { CART_I18NEXT } from '#app/routes/($lang).cart/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'

/* -------------------------------------------------------------------------------------------------
 * CheckoutLink
 * -----------------------------------------------------------------------------------------------*/

type CheckoutLinkProps = {
	className?: string
	locale: Locale
	setApp: Dispatch<SetStateAction<App>>
	popover?: boolean
	optimisticItemsTotal: number
	optimisticShippingTotal: number
	optimisticTaxTotal: number
	optimisticTotal: number
}

const CheckoutLink: FC<CheckoutLinkProps> = ({
	className,
	locale,
	setApp,
	popover = true,
	optimisticItemsTotal,
	optimisticShippingTotal,
	optimisticTaxTotal,
	optimisticTotal,
}) => {
	const [animate, setAnimate] = React.useState(false)
	const { t } = useTranslation(CART_I18NEXT.routeCart)
	const location = useLocation()

	React.useEffect(() => {
		;(async () => {
			await new Promise((resolve) => setTimeout(resolve, 2000))
			setAnimate(true)
		})().catch(console.error)
	}, [])

	const untilFree = appConfig.free_shipping - optimisticItemsTotal

	return (
		<Box className={className}>
			{popover && <Separator size="4" />}
			<Table.Root size="1">
				<Table.Body>
					<Table.Row>
						<Table.Cell>
							<Text color="gray" size="2" weight="medium">
								{t('label.subtotal')}
							</Text>
						</Table.Cell>
						<Table.Cell justify="end">
							<Price locale={locale} color="gray" size="2" weight="medium">
								{optimisticItemsTotal}
							</Price>
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell>
							<Text color="gray" size="2" weight="medium">
								{t('label.shipping')}
							</Text>
						</Table.Cell>
						<Table.Cell justify="end">
							<Price locale={locale} color="gray" size="2" weight="medium">
								{optimisticShippingTotal}
							</Price>
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell>
							<Text color="gray" size="2" weight="medium">
								{t('label.taxes')}
							</Text>
						</Table.Cell>
						<Table.Cell justify="end">
							<Price locale={locale} color="gray" size="2" weight="medium">
								{optimisticTaxTotal}
							</Price>
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table.Root>
			<Separator size="4" />
			<Table.Root size="1">
				<Table.Body>
					<Table.Row>
						<Table.Cell>
							<Text size="3" weight="bold">
								{t('label.total')}
							</Text>
						</Table.Cell>
						<Table.Cell justify="end">
							<Price locale={locale} size="3" weight="bold">
								{optimisticTotal}
							</Price>
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table.Root>
			{untilFree > 0 && (
				<>
					<Separator size="4" />
					<Table.Root size="1">
						<Table.Body>
							<Table.Row>
								<Table.Cell>
									<Flex asChild align="center" gapX="2">
										<Text color="green" weight="bold" size="3">
											{t('checkoutForm.common.freeShipping')}
											<TransportFree
												width={40}
												height={40}
												className="icon -my-4 fill-transparent stroke-accent-8 stroke-12 transition-all duration-700 ease-in-out"
												animate={animate}
											/>
										</Text>
									</Flex>
								</Table.Cell>
								<Table.Cell justify="end">
									<Price locale={locale} color="green" weight="bold" size="3">
										{untilFree}
									</Price>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table.Root>
				</>
			)}
			<Link
				to={`/${locale}/checkout`}
				prefetch="viewport"
				onClick={() =>
					setApp((prev) => ({
						...prev,
						displaySidebar: false,
					}))
				}
				className={cn('mt-5 flex flex-col items-stretch', {
					'pointer-events-none': location.pathname.includes('/checkout'),
				})}
			>
				<Button size="4">
					{t('checkoutForm.button.proceedToCheckoutSubmit')}
				</Button>
			</Link>
		</Box>
	)
}
CheckoutLink.displayName = 'CheckoutLink'

/* -----------------------------------------------------------------------------------------------*/

export { type CheckoutLinkProps, CheckoutLink }
